import { useState } from 'react';
import { useI18n } from '@/i18n';
import {
  Badge,
  Button,
  Card,
  Input,
  Label,
  Spinner,
} from '@/components/ui';
import {
  useCACAllPublicationRequests,
  useCACApprovePublication,
} from '@/features/cac';
import type { CACPublicationRequest } from '@/features/cac';
import { CheckCircle, ClipboardList } from 'lucide-react';

function publicErrorMessage(error: unknown): string {
  const defaultMessage = 'Operation failed. Please retry.';
  if (!error || typeof error !== 'object') return defaultMessage;
  const maybeAxios = error as { response?: { data?: { detail?: { code?: string; message?: string } } } };
  const detail = maybeAxios.response?.data?.detail;
  if (!detail?.code) return defaultMessage;
  switch (detail.code) {
    case 'CAC_HARBOR_DIGEST_MISSING': return 'A Harbor digest is required to approve this request.';
    case 'CAC_NOT_FOUND': return 'Publication request not found.';
    case 'CAC_BILLING_REGISTRATION_FAILED': return 'Billing registration failed. Check AR service.';
    default: return detail.message || defaultMessage;
  }
}

function statusBadge(req: CACPublicationRequest) {
  if (req.status === 'published') return <Badge variant="default">published</Badge>;
  if (req.status === 'failed' || req.status === 'rejected') return <Badge variant="destructive">{req.status}</Badge>;
  if (req.status === 'approved') return <Badge variant="secondary">approved</Badge>;
  return <Badge variant="outline">{req.status}</Badge>;
}

function ApprovalRow({ req }: { req: CACPublicationRequest }) {
  const [digest, setDigest] = useState('');
  const [approvedLevel, setApprovedLevel] = useState<'free' | 'commercial'>(req.requested_level);
  const approve = useCACApprovePublication();

  const canApprove = req.status === 'pending' || req.status === 'queued';

  const handleApprove = async () => {
    await approve.mutateAsync({
      requestId: req.id,
      approved_level: approvedLevel,
      harbor_digest: digest || undefined,
    });
  };

  return (
    <Card className="p-4 space-y-3" data-testid="cac-approval-row">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <p className="font-mono text-xs truncate text-muted-foreground">{req.id}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">artifact</span>
            <span className="font-mono text-xs">{req.artifact_id}</span>
            <Badge variant="outline">{req.requested_level}</Badge>
            <Badge
              variant={req.delivery_mode === 'use_only' ? 'secondary' : 'outline'}
              title="Delivery mode declarado por el publisher"
            >
              {req.delivery_mode === 'use_only' ? 'use only' : 'installable'}
            </Badge>
            {statusBadge(req)}
          </div>
          <p className="text-xs text-muted-foreground">
            Tenant: <span className="font-mono">{req.requester_tenant_id}</span>
            {' · '}
            {new Date(req.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      {canApprove ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-border/40">
          <div className="space-y-1">
            <Label htmlFor={`level-${req.id}`}>Approved level</Label>
            <select
              id={`level-${req.id}`}
              value={approvedLevel}
              onChange={(e) => setApprovedLevel(e.target.value as 'free' | 'commercial')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="free">Free</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor={`digest-${req.id}`}>Harbor digest (required for non-draft approval)</Label>
            <Input
              id={`digest-${req.id}`}
              placeholder="sha256:…"
              value={digest}
              onChange={(e) => setDigest(e.target.value)}
            />
          </div>
        </div>
      ) : null}

      {approve.isError ? (
        <p className="text-sm text-destructive">{publicErrorMessage(approve.error)}</p>
      ) : null}
      {approve.isSuccess ? (
        <p className="text-sm text-green-700 dark:text-green-400">Approved successfully.</p>
      ) : null}

      {canApprove ? (
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={approve.isPending}
            onClick={() => void handleApprove()}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Approve
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

export function CACApprovalsPage() {
  const { t: _t } = useI18n();
  void _t;

  const allRequestsQuery = useCACAllPublicationRequests();
  const requests = allRequestsQuery.data ?? [];

  const pending = requests.filter((r: CACPublicationRequest) => r.status === 'pending' || r.status === 'queued');
  const rest = requests.filter((r: CACPublicationRequest) => r.status !== 'pending' && r.status !== 'queued');

  return (
    <div className="space-y-6" data-testid="cac-approvals-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-primary" />
          CAC Publication Approvals
        </h1>
        <p className="text-muted-foreground mt-1">
          Review and approve artifact publication requests. Platform admin operation.
        </p>
      </div>

      {allRequestsQuery.isLoading ? (
        <div className="flex items-center gap-2 py-16">
          <Spinner />
        </div>
      ) : allRequestsQuery.isError ? (
        <Card className="p-4 border-destructive/30 text-destructive">
          Failed to load publication requests.
        </Card>
      ) : null}

      {!allRequestsQuery.isLoading && pending.length === 0 && rest.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          No publication requests found.
        </Card>
      ) : null}

      {pending.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            Pending review
            <Badge variant="secondary">{pending.length}</Badge>
          </h2>
          {pending.map((req: CACPublicationRequest) => (
            <ApprovalRow key={req.id} req={req} />
          ))}
        </section>
      ) : null}

      {rest.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-muted-foreground">History</h2>
          {rest.map((req: CACPublicationRequest) => (
            <ApprovalRow key={req.id} req={req} />
          ))}
        </section>
      ) : null}
    </div>
  );
}
